import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { finalize } from 'rxjs';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NcpbService } from '@core/services/ncpb.service';
import { PsDocument, PsDocumentGroup } from '@core/interfaces/eu/ps-document';

import { TranslateService, Translation } from '@ngx-translate/core';

@Component({
  selector: 'ncp-eu-ps',
  imports: [ProgressSpinner],
  templateUrl: './ps.component.html',
})
export class PsComponent implements OnInit {
  private readonly ncpbService = inject(NcpbService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);

  protected translations: Translation = this.translate.instant('eu');

  protected loading = false;
  protected documentLoading = false;
  protected documents: PsDocumentGroup[] = [];

  protected pdfUrl?: SafeResourceUrl;
  protected htmlUrl?: SafeResourceUrl;

  private selectedCountry = JSON.parse(sessionStorage.getItem('selectedCountry') || '{}');
  private patient = JSON.parse(sessionStorage.getItem('euPatient') || '{}');

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => {
      this.translations = this.translate.instant('eu');
    });

    this.retrieve();
  }

  private retrieve() {
    this.loading = true;
    this.cdr.detectChanges();

    this.ncpbService
      .ps({
        countryCode: this.selectedCountry.countryCode,
        patientIdentifier: this.patient.identifier,
        purposeOfUse: 'TREATMENT',
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any[]) => {
          const flatDocuments: PsDocument[] = (res || [])
            .filter((item: any) => item?.clinicalDocuments?.length > 0)
            .map((item: any) => {
              const document = item.clinicalDocuments[0];

              return {
                patientIdentifier: item.patientIdentifier,
                nextOfKin: item.nextOfKin,
                identifier: document.identifier,
                repositoryId: document.repositoryId,
                homeCommunityId: document.homeCommunityId,
                name: document.name,
                title: document.title,
                formatCode: document.formatCode,
                mimeType: document.mimeType,
                creationDt: document.creationDt,
              };
            });

          this.documents = this.groupDocuments(flatDocuments);
        },
        error: () => {
          this.documents = [];
        },
      });
  }

  private groupDocuments(documents: PsDocument[]): PsDocumentGroup[] {
    const groups: PsDocumentGroup[] = [];

    documents.forEach((document) => {
      let group = groups.find(
        (x) => x.title === document.title && x.creationDt === document.creationDt
      );

      if (!group) {
        group = {
          title: document.title || document.name,
          creationDt: document.creationDt,
        };

        groups.push(group);
      }

      if (this.isPdf(document)) {
        group.pdfDocument = document;
      } else {
        group.cdaDocument = document;
      }
    });

    return groups;
  }

  protected printPdf = (document: PsDocument) => {
    this.clearDocumentView();
    this.documentLoading = true;
    this.cdr.detectChanges();

    this.ncpbService
      .psRetrieve({
        countryCode: this.selectedCountry.countryCode,
        nextOfKin: document.nextOfKin,
        patientIdentifier: document.patientIdentifier,
        purposeOfUse: 'TREATMENT',
        repositoryId: document.repositoryId,
        documentIdentifier: document.identifier,
        homeCommunityId: document.homeCommunityId,
      })
      .pipe(
        finalize(() => {
          this.documentLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          const pdfContent =
            res?.pdfContent || res?.content || res?.data?.pdfContent || res?.data?.content;

          if (pdfContent) {
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              `data:application/pdf;base64,${pdfContent}`
            );
          }
        },
        error: () => {
          this.clearDocumentView();
        },
      });
  };

  protected displayCda = (document: PsDocument) => {
    this.clearDocumentView();
    this.documentLoading = true;
    this.cdr.detectChanges();

    this.ncpbService
      .psRetrieveDisplay({
        countryCode: this.selectedCountry.countryCode,
        nextOfKin: document.nextOfKin,
        patientIdentifier: document.patientIdentifier,
        purposeOfUse: 'TREATMENT',
        repositoryId: document.repositoryId,
        documentIdentifier: document.identifier,
        homeCommunityId: document.homeCommunityId,
      })
      .subscribe({
        next: (html: string) => {
          const blob = new Blob([html], {
            type: 'text/html',
          });

          const url = URL.createObjectURL(blob);

          this.htmlUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

          this.documentLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.clearDocumentView();

          this.documentLoading = false;
          this.cdr.detectChanges();
        },
      });
  };

  protected isPdf(document: PsDocument): boolean {
    return document.formatCode?.toLowerCase().includes('pdf');
  }

  private clearDocumentView() {
    this.pdfUrl = undefined;
    this.htmlUrl = undefined;
  }
}
